import { useState } from 'react';
import gql from 'graphql-tag';
import { useApolloClient } from '@apollo/client';
import { phoneWithInternationalCode } from '../utils/helpers/common';
import { createInteractionMutation } from '../api/mutations';
import useScreenSize from './useScreenSize';
import {
  InteractionEventTypeEnum,
  InteractionTargetTypeEnum
} from '../graphql/types';
import { useAnalyticLogSource } from './useAnalyticLogSource';
import { CreateInteractionMutation } from '../graphql/graphql';

const query = gql`
  query ($path: Path!) {
    phones @rest(type: "Phones", path: $path) {
      phones
    }
  }
`;

interface UseFullPhoneNumbersProps {
  id?: string | string[];
  residenceId: string | null;
  path: string;
  targetType: InteractionTargetTypeEnum;
  agencyId: string | null;
  setErrorUI?: (value: boolean) => void;
}

export enum PhoneButtonTargetTypeEnum {
  gallery = 'gallery',
  main = 'main',
  contact = 'contact',
  layout = 'layout'
}

export const useFullPhoneNumbers = ({
  id,
  residenceId,
  path,
  targetType,
  agencyId,
  setErrorUI
}: UseFullPhoneNumbersProps) => {
  const client = useApolloClient();
  const analyticLogSource = useAnalyticLogSource();

  const [phonesShown, setPhonesShown] = useState<string[] | null>(null);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { isSmallOrLargeDesktop } = useScreenSize();

  const isCurrentItemPage = targetType === 'ITEM';
  const isAgencyPage = targetType === 'AGENCY';
  const isResidencePage = targetType === 'RESIDENCE';
  let targetId: string | string[] | null | undefined;

  switch (true) {
    case isAgencyPage:
      targetId = agencyId;
      break;
    case isResidencePage:
      targetId = residenceId;
      break;
    case isCurrentItemPage:
      targetId = id;
      break;
    default:
      targetId = id;
  }

  const callPhones = (
    phones: string[] | undefined = [],
    index: number,
    triggerButton: PhoneButtonTargetTypeEnum | null,
    isCalled: boolean
  ) => {
    if (
      (isResidencePage && isCalled) ||
      (triggerButton === PhoneButtonTargetTypeEnum.gallery &&
        isSmallOrLargeDesktop &&
        phones?.length === 1) ||
      (!isAgencyPage &&
        triggerButton !== PhoneButtonTargetTypeEnum.gallery &&
        isSmallOrLargeDesktop)
    ) {
      return;
    }

    if (
      (phones?.length || 0) > 1 &&
      triggerButton !== PhoneButtonTargetTypeEnum.contact
    ) {
      setIsVisible(true);
    } else {
      window.open(`tel:${phoneWithInternationalCode(phones?.[index])}`, '_top');
    }
  };

  const sendAnalyticsData = (
    triggerButton: PhoneButtonTargetTypeEnum | null,
    phones: string[] | undefined
  ) => {
    try {
      client.mutate<CreateInteractionMutation>({
        mutation: createInteractionMutation,
        variables: {
          targetId,
          targetType,
          sourceLink: `${window.location?.origin}${window.location?.pathname}`,
          triggerButton,
          phones,
          eventType: InteractionEventTypeEnum.ShowPhones,
          source: analyticLogSource
        },
        fetchPolicy: 'network-only'
      });
    } catch (error) {
      setErrorUI?.(true);
      console.log(`Error occurred: ${error}`);
    }
  };

  const fetchData = async (
    index = 0,
    isCalled: boolean,
    triggerButton: PhoneButtonTargetTypeEnum | null
  ) => {
    try {
      client
        .query({
          query,
          variables: {
            path: `${path}/${id}/phones?react=true`
          },
          fetchPolicy: 'network-only'
        })
        .then(response => {
          const { phones } = response?.data;
          if (phones && phones?.phones) {
            callPhones(phones.phones, index, triggerButton, isCalled);

            sendAnalyticsData(triggerButton, phones.phones);
            setPhonesShown(phones.phones);
          } else {
            setShowAlertMessage(true);
          }
        })
        .catch(() => {
          setErrorUI?.(true);
        });
    } catch (error) {
      console.log(`Error occurred: ${error}`);
    }
  };

  const onClickHandler =
    (index = 0, isCalled: boolean) =>
    async (
      e: React.MouseEvent<HTMLElement>,
      triggerButton: PhoneButtonTargetTypeEnum | null = null,
      gallery = false
    ) => {
      if (!gallery) {
        e.preventDefault();
        e.persist();
      }

      if (phonesShown) {
        callPhones(phonesShown, index, triggerButton, isCalled);
        return null;
      }
      await fetchData(index, isCalled, triggerButton);
    };

  return {
    onClickHandler,
    phonesShown,
    setPhonesShown,
    setIsVisible,
    isVisible,
    showAlertMessage
  };
};
